import { apiClient } from "@/services/api.client"
import { newRequestId, noteTurnRequestId } from "@/lib/requestIds"
import { env } from "@/lib/env"
import { resolveApiUrl } from "@/lib/api-url"
import { NiraHttpError, streamEvents, type StreamEvent, type StreamResult } from "./stream"
import type { CaseFileDetail, CaseFileListRow, CaseFileRow, ConfirmedNotice, DocRow, FeatureTag, MatterContents, MatterSessionType, MovePreview, NoticeRead, NoticeState, ReadingView, RecentReading, RouteDecision, ThreadRow, TurnRequest } from "./contract"
import type { ThreadContext } from "./readerContext"
import type { DocumentRow as SimDocumentRow } from "../oio-sim/contract"

import { NIRA_BASE as BASE, GUEST_BASE } from "@/lib/authGate"
import { getTurnstileToken, TURNSTILE_HEADER } from "@/lib/turnstile"

const enc = encodeURIComponent
const url = (path: string, base: string = BASE) => resolveApiUrl(env.VITE_API_URL, `${base}${path}`)

type OnEvent = (event: StreamEvent) => void

export const niraV2 = {
  /**
   * A new thread. `caseId` puts it in that workspace (Nira stamps its feature from the case); `feature`
   * is a hint, checked against the case by the server.
   */
  createThread: async (title?: string, ctx?: Pick<ThreadContext, "side" | "state_code" | "state_source">, base: string = BASE, opts: { caseId?: string | null; feature?: FeatureTag } = {}) => {
    // The guest's free search carries a fresh bot-check token (single-use); with none, the server decides.
    const token = base === GUEST_BASE ? await getTurnstileToken() : null
    return apiClient.post<ThreadRow>(`${base}/threads`, {
      ...(title ? { title } : {}),
      ...(ctx ? { side: ctx.side, state_code: ctx.state_code, state_source: ctx.state_source === "chosen" || !ctx.state_source ? "chosen" : ctx.state_source === "none" ? "none" : "last" } : {}),
      ...(opts.caseId ? { case_id: opts.caseId } : {}),
      ...(opts.feature ? { feature: opts.feature } : {}),
    }, token ? { headers: { [TURNSTILE_HEADER]: token } } : undefined)
  },
  patchThread: (id: string, ctx: Partial<Pick<ThreadContext, "side" | "state_code">>) =>
    apiClient.patch<ThreadRow>(`${BASE}/threads/${enc(id)}`, ctx),
  listThreads: () => apiClient.get<ThreadRow[]>(`${BASE}/threads`),
  deleteThread: (id: string) => apiClient.delete<unknown>(`${BASE}/threads/${enc(id)}`),
  getThread: (id: string, base: string = BASE) => apiClient.get<unknown>(`${base}/threads/${enc(id)}`),
  /**
   * Uploaded as they are. On-device masking (./mask) is off for Nira for individuals: OCR of a
   * scanned notice on an ordinary laptop or phone took minutes, and the reading's accuracy comes
   * first. It is kept for Nira for firms; to turn it back on, run the files through
   * `maskFiles(files, onProgress)` from "./mask" and send its counts as the "masked" field.
   */
  uploadDocuments: async (threadId: string, files: File[], _onProgress?: (line: string) => void) => {
    const form = new FormData()
    files.forEach((file) => form.append("files", file))
    form.append("thread_id", threadId)
    return apiClient.upload<DocRow[]>(`${BASE}/documents`, form)
  },
  /** Before a question is sent: research, or a notice or an order to read in its own door. */
  routeQuestion: (threadId: string, message: string, docIds: string[]) =>
    apiClient.post<RouteDecision>(`${BASE}/threads/${enc(threadId)}/route`, { message, doc_ids: docIds }),
  listDocuments: (threadId: string) => apiClient.get<DocRow[]>(`${BASE}/documents`, { thread_id: threadId }),
  deleteDocument: (id: string) => apiClient.delete<unknown>(`${BASE}/documents/${enc(id)}`),
  listCases: () => apiClient.get<CaseFileListRow[]>(`${BASE}/cases`),
  listRecentReadings: (kind: "all" | "notice" | "appeal" = "all", limit = 20) => apiClient.get<RecentReading[]>(`${BASE}/readings/recent`, { kind, limit: String(limit) }),
  getCase: (id: string) => apiClient.get<CaseFileDetail>(`${BASE}/cases/${enc(id)}`),
  createCase: (body: { title?: string; thread_id?: string }) => apiClient.post<CaseFileRow>(`${BASE}/cases`, body),
  patchCase: (id: string, body: Partial<Pick<CaseFileRow, "title" | "keep_after_close" | "allow_training" | "closed">>) =>
    apiClient.patch<CaseFileRow>(`${BASE}/cases/${enc(id)}`, body),
  deleteCase: (id: string) => apiClient.delete<unknown>(`${BASE}/cases/${enc(id)}`),
  getNotice: (caseId: string, doc?: string) => apiClient.get<NoticeState>(`${BASE}/cases/${enc(caseId)}/notice`, doc ? { doc } : undefined),
  rereadNotice: (caseId: string) => apiClient.post<NoticeRead>(`${BASE}/cases/${enc(caseId)}/notice/read`, {}),
  confirmNotice: (caseId: string, docId: string, notice: ConfirmedNotice) =>
    apiClient.put<CaseFileRow>(`${BASE}/cases/${enc(caseId)}/notice`, { doc_id: docId, ...notice }),
  attachThread: (caseId: string, threadId: string) =>
    apiClient.put<unknown>(`${BASE}/cases/${enc(caseId)}/threads/${enc(threadId)}`, {}),
  startReading: (caseId: string, kind: "notice_no_reply" | "order_preview" = "notice_no_reply", extra: { communicated_on?: string; with_documents?: boolean; doc?: string } = {}) =>
    apiClient.post<ReadingView>(`${BASE}/cases/${enc(caseId)}/readings`, { kind, ...extra }),
  getReading: (caseId: string, readingId: string) =>
    apiClient.get<ReadingView>(`${BASE}/cases/${enc(caseId)}/readings/${enc(readingId)}`),
  /** The officer's reading on a finished notice reading: three runs, landing on it as `officer`. */
  startOfficerReading: (caseId: string, readingId: string) =>
    apiClient.post<ReadingView>(`${BASE}/cases/${enc(caseId)}/readings/${enc(readingId)}/officer`, {}),
  /** An order draft started from the case file gets its documents; the simulator reads each as a drop. */
  documentsToDraft: (caseId: string, order: "oio" | "oia", threadId: string) =>
    apiClient.post<{ documents: SimDocumentRow[]; failed: string[] }>(`${BASE}/cases/${enc(caseId)}/documents/to-draft`, { order, thread_id: threadId }),
  createShare: (threadId: string, body: ShareRequest) =>
    apiClient.post<ShareCreated>(`${BASE}/threads/${enc(threadId)}/shares`, body),
  /** A Matter's contents: its case view, its sessions, and the shelf of files they carry. */
  getMatterContents: (caseId: string) => apiClient.get<MatterContents>(`${BASE}/cases/${enc(caseId)}/contents`),
  /** What moving a session into this Matter would bring with it. */
  previewMove: (caseId: string, type: MatterSessionType, sessionId: string) =>
    apiClient.get<MovePreview>(`${BASE}/cases/${enc(caseId)}/sessions/${type}/${enc(sessionId)}/preview`),
  /** Moves a session into the Matter, or between Matters. */
  moveSession: (caseId: string, type: MatterSessionType, sessionId: string) =>
    apiClient.put<{ matter_files: number; session_files: number }>(`${BASE}/cases/${enc(caseId)}/sessions/${type}/${enc(sessionId)}`, {}),
  /** Moves a session out of the Matter. */
  removeSession: (caseId: string, type: MatterSessionType, sessionId: string) =>
    apiClient.delete<unknown>(`${BASE}/cases/${enc(caseId)}/sessions/${type}/${enc(sessionId)}`),
  /** Files added straight to the Matter's shelf, not to a session. */
  uploadMatterFiles: async (caseId: string, files: File[]) => {
    const form = new FormData()
    files.forEach((file) => form.append("files", file))
    return apiClient.upload<{ doc_id: string; filename: string; status: string }[]>(`${BASE}/cases/${enc(caseId)}/files`, form)
  },
  /** Puts a file in a proceeding by hand (null clears the choice). Answers with the updated contents. */
  linkFile: (caseId: string, docId: string, proceedingId: string | null) =>
    apiClient.post<MatterContents>(`${BASE}/cases/${enc(caseId)}/files/${enc(docId)}/link`, { proceeding_id: proceedingId }),
  deleteMatterFile: (caseId: string, docId: string) => apiClient.delete<unknown>(`${BASE}/cases/${enc(caseId)}/files/${enc(docId)}`),
  /** "Is this result correct?": a reader's vote on a case's shown result, recorded against the case. */
  sendVerification: (threadId: string, body: { turn_id: string; source_id: string; verdict: "confirmed" | "contradicted" | "unclear"; note?: string }) =>
    apiClient.post<{ ok: boolean }>(`${BASE}/threads/${enc(threadId)}/verifications`, body),
}

export interface ShareRequest { turn_id?: string; name?: string; firm?: string; client_view?: boolean; card_jpeg?: string }
export interface ShareCreated { token: string; path: string; client_view: boolean }

/** A shared answer, read without signing in: plain fetch, so a 401 never signs the viewer out. */
export async function getShared(token: string): Promise<unknown> {
  const res = await fetch(url(`/shared/${enc(token)}`), { headers: { Accept: "application/json" } })
  if (!res.ok) throw new NiraHttpError(res.status, res.status === 404 ? "not_found" : "unavailable")
  return res.json()
}

export const createThread = niraV2.createThread
export const listThreads = niraV2.listThreads
export const deleteThread = niraV2.deleteThread
export const getThread = niraV2.getThread
export const patchThread = niraV2.patchThread
export const uploadDocuments = niraV2.uploadDocuments
export const listDocuments = niraV2.listDocuments
export const deleteDocument = niraV2.deleteDocument
export const createShare = niraV2.createShare
export const listCases = niraV2.listCases
export const listRecentReadings = niraV2.listRecentReadings
export const getCase = niraV2.getCase
export const createCase = niraV2.createCase
export const patchCase = niraV2.patchCase
export const deleteCase = niraV2.deleteCase
export const getNotice = niraV2.getNotice
export const rereadNotice = niraV2.rereadNotice
export const confirmNotice = niraV2.confirmNotice
export const startReading = niraV2.startReading
export const routeQuestion = niraV2.routeQuestion
export const attachThread = niraV2.attachThread
export const getReading = niraV2.getReading
export const startOfficerReading = niraV2.startOfficerReading
export const documentsToDraft = niraV2.documentsToDraft
export const getMatterContents = niraV2.getMatterContents
export const previewMove = niraV2.previewMove
export const moveSession = niraV2.moveSession
export const removeSession = niraV2.removeSession
export const uploadMatterFiles = niraV2.uploadMatterFiles
export const linkFile = niraV2.linkFile
export const deleteMatterFile = niraV2.deleteMatterFile
export const sendVerification = niraV2.sendVerification

export function startTurn(threadId: string, body: TurnRequest, onEvent: OnEvent, signal?: AbortSignal, base: string = BASE) {
  const requestId = newRequestId()
  noteTurnRequestId(threadId, requestId)
  return streamEvents(
    url(`/threads/${enc(threadId)}/turns`, base),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        "X-Request-Id": requestId,
        "X-Session-Id": threadId,
      },
      body: JSON.stringify(body),
    },
    onEvent,
    signal,
  )
}

export function followTurn(threadId: string, onEvent: OnEvent, signal?: AbortSignal, base: string = BASE) {
  return streamEvents(
    url(`/threads/${enc(threadId)}/events`, base),
    { headers: { Accept: "text/event-stream", "X-Session-Id": threadId } },
    onEvent,
    signal,
  )
}

export function followDocuments(threadId: string, onEvent: OnEvent, signal?: AbortSignal) {
  return streamEvents(
    url(`/documents/events?thread_id=${enc(threadId)}`),
    { headers: { Accept: "text/event-stream", "X-Session-Id": threadId } },
    onEvent,
    signal,
  )
}

const RESUME_DELAYS_MS = [1000, 2000, 4000]
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * startTurn, then resume via the replay route if the stream drops before a terminal event.
 * Replay is idempotent (the turn reducer drops events with an old seq). HTTP errors are not retried.
 */
export async function startTurnWithResume(
  threadId: string,
  body: TurnRequest,
  onEvent: OnEvent,
  signal?: AbortSignal,
  delaysMs: number[] = RESUME_DELAYS_MS,
  base: string = BASE,
): Promise<StreamResult> {
  const attempt = async (run: () => Promise<StreamResult>): Promise<StreamResult> => {
    try {
      return await run()
    } catch (err) {
      if (err instanceof NiraHttpError || signal?.aborted) throw err
      return { ended: "eof", lastSeq: -1 } // network drop: treat as a stream that ended early
    }
  }
  let result = await attempt(() => startTurn(threadId, body, onEvent, signal, base))
  for (const delay of delaysMs) {
    if (result.ended !== "eof") return result
    await sleep(delay)
    if (signal?.aborted) return result
    const lastSeq = result.lastSeq
    const next = await attempt(() => followTurn(threadId, onEvent, signal, base))
    result = { ended: next.ended, lastSeq: Math.max(lastSeq, next.lastSeq) }
  }
  return result
}
